import React, { useEffect } from 'react';
import ToggleV2 from '@zola/zola-ui/src/components/ToggleV2';
import { LabelSideVariant } from '@zola/zola-ui/src/components/ToggleV2/ToggleV2';
import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';

import { getRegistryDetails, updateRegistryPublish } from 'actions/RegistryActions';
import { showModal } from 'actions/ModalActions';
import { closeSideDrawer } from 'actions/SideDrawerActions';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'reducers/index';
import featureFlags from 'util/featureFlags';

import { StyledMainActionButton } from './RegistryVisibilityToggle.styles';

const {
  EVENTS: { MODAL_TYPE_SET_SHIPPING_ADDRESS, MODAL_TYPE_PUBLISH_REGISTRY },
} = NAV_DATA;

type RegistryVisibilityToggleProps = {
  label?: React.ReactNode;
  variation?: 'button';
};

export const RegistryVisibilityToggle = ({
  label,
  variation,
}: RegistryVisibilityToggleProps): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const registryDetails = useSelector((state: RootState) => state.registry);

  useEffect(() => {
    dispatch(getRegistryDetails()).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clickPublishRegistry = () => {
    if (!registryDetails?.public) {
      if (!registryDetails?.has_shipping_address) {
        return dispatch(
          showModal(MODAL_TYPE_SET_SHIPPING_ADDRESS, {
            size: 'md',
            ariaLabel: 'Set Shipping Address',
          })
        );
      }
      return dispatch(updateRegistryPublish(true)).then(() => {
        dispatch(
          featureFlags.get('registryShareOptions')
            ? showModal('POST_PUBLISH_SHARING', {}, { size: 'md', ariaLabel: 'Registry Published' })
            : showModal(
                MODAL_TYPE_PUBLISH_REGISTRY,
                { slug: registryDetails?.key },
                { size: 'md', ariaLabel: 'Registry Published' }
              )
        );
        dispatch(closeSideDrawer());
      });
    }

    return Promise.resolve();
  };

  const clickUnpublishRegistry = () => {
    if (registryDetails?.public) {
      return dispatch(updateRegistryPublish(false));
    }
    return Promise.resolve();
  };

  const handleChange = () =>
    registryDetails?.public ? clickUnpublishRegistry() : clickPublishRegistry();

  const renderToggle = () => (
    <ToggleV2
      id="registry-visibility-toggle"
      inline
      label={label || 'Visible'}
      labelSide={LabelSideVariant.RIGHT}
      value={registryDetails?.public}
      onChange={handleChange}
    />
  );

  // Button variation is currently used on mweb & tablet post-auth nav
  if (variation === 'button') {
    return (
      <StyledMainActionButton onClick={handleChange} variant="secondary">
        {renderToggle()}
      </StyledMainActionButton>
    );
  }

  // Default is to just render the toggle
  return renderToggle();
};

export default RegistryVisibilityToggle;
